import { Controller } from "stimulus"

function getCsrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.content : ''
}

export default class extends Controller {
  static targets = ["tipArea", "startArea", "tipContent", "tipTitle", "tipBody", "achievementsSection", "tipPosition", "prevButton", "tipLesson", "tipLessonName", "learnForm"]

  currentTipId = null
  currentIndex = 0
  totalTips = 0

  connect() {
    // Initialize with pre-loaded tip if available
    if (this.hasTipContentTarget && this.tipContentTarget.dataset.tipId) {
      this.currentTipId = this.tipContentTarget.dataset.tipId
    }
  }

  async loadTip() {
    try {
      const url = this.currentTipId
        ? `/learning_tips/next?current_tip_id=${this.currentTipId}`
        : '/learning_tips/next'

      const response = await fetch(url, {
        headers: { 'Accept': 'application/json' }
      })

      if (response.ok) {
        const tip = await response.json()
        this.displayTip(tip)
      }
    } catch (e) {
      console.error('Failed to fetch tip:', e)
    }
  }

  async loadPreviousTip() {
    if (!this.currentTipId) return

    try {
      const response = await fetch(`/learning_tips/previous?current_tip_id=${this.currentTipId}`, {
        headers: { 'Accept': 'application/json' }
      })

      if (response.ok) {
        const tip = await response.json()
        this.displayTip(tip)
      }
    } catch (e) {
      console.error('Failed to fetch previous tip:', e)
    }
  }

  displayTip(tip) {
    this.currentTipId = tip.id
    this.currentIndex = tip.index || 0
    this.totalTips = tip.total || 0

    // Update tip content
    this.tipTitleTarget.textContent = tip.title
    this.tipBodyTarget.textContent = tip.content

    // Update position indicator
    if (this.hasTipPositionTarget && this.totalTips > 0) {
      this.tipPositionTarget.textContent = `${this.currentIndex} of ${this.totalTips}`
    }

    // Show/hide previous button based on position
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.classList.toggle('d-none', this.currentIndex <= 1)
    }

    // Show lesson name if available
    if (this.hasTipLessonTarget && this.hasTipLessonNameTarget) {
      if (tip.lesson) {
        this.tipLessonNameTarget.textContent = tip.lesson.title
        this.tipLessonTarget.classList.remove('d-none')
      } else {
        this.tipLessonTarget.classList.add('d-none')
      }
    }

    // Update the learn link href URL
    if (this.hasLearnFormTarget) {
      this.learnFormTarget.href = `/learning_tips/${tip.id}/start_conversation`
    }

    // Hide start area if it exists, show tip content
    if (this.hasStartAreaTarget) {
      this.startAreaTarget.classList.add('d-none')
    }
    this.tipContentTarget.classList.remove('d-none')

    // Hide achievements section when tip is showing
    if (this.hasAchievementsSectionTarget) {
      this.achievementsSectionTarget.classList.add('d-none')
    }
  }

  async markViewed() {
    if (!this.currentTipId) return

    try {
      const response = await fetch(`/learning_tips/${this.currentTipId}/view`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': getCsrfToken()
        }
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      }
    } catch (e) {
      console.error('Failed to record view:', e)
    }

    // Show next tip or return to start
    this.loadTip()
  }

  async learnWithAI() {
    if (!this.currentTipId) return

    // Show loading state
    const button = this.tipContentTarget.querySelector('[data-action="click->learning-card#learnWithAI"]')
    const originalContent = button.innerHTML
    button.innerHTML = '<span class="far fa-spinner fa-spin mr-1"></span>Starting...'
    button.disabled = true

    try {
      const response = await fetch(`/learning_tips/${this.currentTipId}/start_conversation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        }
      })

      if (response.ok) {
        const data = await response.json()
        // Navigate to the new thread
        window.location.href = data.redirect_url
      } else {
        throw new Error('Failed to start conversation')
      }
    } catch (e) {
      console.error('Failed to start conversation:', e)
      button.innerHTML = originalContent
      button.disabled = false
      this.showError('Failed to start learning conversation. Please try again.')
    }
  }

  async nextTip() {
    if (this.currentTipId) {
      // Record view of current tip before loading next
      try {
        const response = await fetch(`/learning_tips/${this.currentTipId}/view`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/vnd.turbo-stream.html',
            'X-CSRF-Token': getCsrfToken()
          }
        })

        if (response.ok) {
          const html = await response.text()
          Turbo.renderStreamMessage(html)
        }
      } catch (e) {
        // Silent fail
      }
    }

    this.loadTip()
  }

  async restart() {
    try {
      const response = await fetch('/learning_tips/restart', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': getCsrfToken()
        }
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      }
    } catch (e) {
      console.error('Failed to restart:', e)
    }
  }

  showError(message) {
    const errorToast = document.createElement('div')
    errorToast.className = 'alert alert-danger position-fixed'
    errorToast.style.cssText = 'bottom: 24px; right: 24px; z-index: 10000;'
    errorToast.textContent = message
    document.body.appendChild(errorToast)
    setTimeout(() => errorToast.remove(), 3000)
  }
}
